import { AssetType } from '@amzn/fire-tv-campaign-automation-service';
import { useCallback, useMemo } from 'react';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { createAssetHandler } from 'src/handlers/factories/assetHandlerFactory';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetState } from 'src/types';
import { AssetInput } from './assets/AssetInput';
import { ASSET_CONFIG } from 'src/configs/assetConfig';
import styles from './AssetInputSection.module.css';

export const AssetInputSection = () => {
  const controlPlaneClient = getControlPlaneClient();
  const { assetStates, updateAssetState } = useAssetContext();

  // Background Image Handler
  const handleBackgroundImageState = useCallback(
    (state: Partial<AssetState>) => {
      updateAssetState(AssetType.BACKGROUND_IMAGE, state);
    },
    [updateAssetState],
  );

  const backgroundImageHandler = useMemo(
    () =>
      createAssetHandler(
        AssetType.BACKGROUND_IMAGE,
        handleBackgroundImageState,
        controlPlaneClient,
      ),
    [handleBackgroundImageState, controlPlaneClient],
  );

  // Logo Handler
  const handleLogoState = useCallback(
    (state: Partial<AssetState>) => {
      updateAssetState(AssetType.LOGO, state);
    },
    [updateAssetState],
  );

  const logoHandler = useMemo(
    () => createAssetHandler(AssetType.LOGO, handleLogoState, controlPlaneClient),
    [handleLogoState, controlPlaneClient],
  );

  // Video Handler
  const handleVideoState = useCallback(
    (state: Partial<AssetState>) => {
      updateAssetState(AssetType.VIDEO_TRAILER, state);
    },
    [updateAssetState],
  );

  const videoHandler = useMemo(
    () => createAssetHandler(AssetType.VIDEO_TRAILER, handleVideoState, controlPlaneClient),
    [handleVideoState, controlPlaneClient],
  );

  return (
    <>
      <div className={styles.assetInputGroup}>
        <AssetInput
          label={ASSET_CONFIG[AssetType.BACKGROUND_IMAGE].displayLabel}
          assetInputHandler={backgroundImageHandler}
          acceptedFileTypes={ASSET_CONFIG[AssetType.BACKGROUND_IMAGE].acceptedFileTypes}
          maxFileSizeInBytes={ASSET_CONFIG[AssetType.BACKGROUND_IMAGE].maxFileSizeInBytes}
          error={assetStates[AssetType.BACKGROUND_IMAGE].error}
          status={assetStates[AssetType.BACKGROUND_IMAGE].status}
          data-testid={ASSET_CONFIG[AssetType.BACKGROUND_IMAGE].dataTestId}
        />
      </div>

      <div className={styles.assetInputGroup}>
        <AssetInput
          label={ASSET_CONFIG[AssetType.LOGO].displayLabel}
          assetInputHandler={logoHandler}
          acceptedFileTypes={ASSET_CONFIG[AssetType.LOGO].acceptedFileTypes}
          maxFileSizeInBytes={ASSET_CONFIG[AssetType.LOGO].maxFileSizeInBytes}
          error={assetStates[AssetType.LOGO].error}
          status={assetStates[AssetType.LOGO].status}
          data-testid={ASSET_CONFIG[AssetType.LOGO].dataTestId}
        />
      </div>

      <div className={styles.assetInputGroup}>
        <AssetInput
          label={ASSET_CONFIG[AssetType.VIDEO_TRAILER].displayLabel}
          assetInputHandler={videoHandler}
          acceptedFileTypes={ASSET_CONFIG[AssetType.VIDEO_TRAILER].acceptedFileTypes}
          maxFileSizeInBytes={ASSET_CONFIG[AssetType.VIDEO_TRAILER].maxFileSizeInBytes}
          error={assetStates[AssetType.VIDEO_TRAILER].error}
          status={assetStates[AssetType.VIDEO_TRAILER].status}
          data-testid={ASSET_CONFIG[AssetType.VIDEO_TRAILER].dataTestId}
        />
      </div>
    </>
  );
};
